import React, { useRef } from "react";
import { Capacitor } from "@capacitor/core";
import InstagramGlyph from "./InstagramGlyph";
import VerifiedCelebration from "./VerifiedCelebration";
import VerifiedTick from "./VerifiedTick";
import { useFitText } from "../hooks/useFitText";
import { onComputer, openInstagramInApp, type InstagramDmVerify } from "../hooks/useInstagramDmVerify";
import { T } from "../i18n/T";
import { t } from "../i18n";
import "../pages/FanConnectFlow.css";

// The "Connect Instagram" screens (the founder's own, polished over rounds
// 1–7, 2026-10-27/28; client/.claude/instagram-connect-fan-flow-r7.html), as
// building blocks: the fan sign-up (pages/FanConnectFlow) and the talent's
// "Verify with a DM" backup use the very same screens with their own words.
//
// One stage for every step: a soft Brand Green light over black, the
// wordmark at the top, the content in the middle, the buttons at the bottom
// under the thumb. Each step is a plain function returning what goes in the
// stage, so both flows render the same elements in the same places.

export type IgConnectView = {
  /** The Back button top left; null = none on this step. */
  back: (() => void) | null;
  content: React.ReactNode;
  footer: React.ReactNode;
};

export const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button type="button" className="fcf-back" onClick={onClick}>
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" />
    </svg>
    {t("fanConnect.back")}
  </button>
);

const Chevron = () => (
  <svg className="fcf-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 5 7 7-7 7" />
  </svg>
);

// The app's duotone green icon family (GreenIcons.tsx): soft fill + 1.8 stroke.
export const BENEFIT_FILL = "rgba(36, 201, 97, 0.22)";
export const BENEFIT_GREEN = "#24c961";
export type IgConnectBenefit = { icon: React.ReactNode; text: string };
const Benefit: React.FC<IgConnectBenefit> = ({ icon, text }) => (
  <li className="fcf-benefit">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {icon}
    </svg>
    <span>{text}</span>
  </li>
);

/** The stage: Back + wordmark on top, the step in the middle, its buttons at the bottom.
    `over`: drawn above the app's sheets (the talent's backup opens over Edit profile).
    `strip`: talent sign-up's "Step 2 of 3" line, under the wordmark (build 54). */
export function igConnectStage(stepKey: string, view: IgConnectView, over = false, strip?: React.ReactNode) {
  return (
    <div className={`fcf-page${over ? " fcf-page--over" : ""}${strip ? " fcf-page--strip" : ""}`}>
      <div className="fcf-head">
        {view.back && <BackButton onClick={view.back} />}
        <img className="fcf-wordmark" src="/request-wordmark.png" alt={t("appStoreDownloadPrompt.requestApp")} />
      </div>
      {strip && <div className="fcf-strip">{strip}</div>}
      <div className="fcf-body" key={stepKey}>
        {view.content}
      </div>
      <div className="fcf-foot">{view.footer}</div>
    </div>
  );
}

/** "Connect Instagram": you + Instagram, the badge by the title, three reasons, one green button. */
export function connectView({
  you,
  benefits,
  v,
  onBack,
  onSkip,
  onConnect,
  busy,
  note,
}: {
  /** The person's bubble (FanAvatar with the fcf-you class). */
  you: React.ReactNode;
  benefits: IgConnectBenefit[];
  v: InstagramDmVerify;
  onBack: () => void;
  onSkip: () => void;
  /** What Connect does; the message round trip when absent. Talent (Edit
      profile's Verify): Meta's Instagram sign-in first, the message only for a personal account. */
  onConnect?: () => void;
  /** The button is held while that starts. */
  busy?: boolean;
  /** One line under the button (how the last try ended); the message's own failure when absent. */
  note?: string;
}): IgConnectView {
  const line = note ?? (v.phase === "failed" ? t("fanConnect.failed") : "");
  return {
    back: onBack,
    content: (
      <>
        <div className="fcf-you-ig">
          {you}
          <span className="fcf-ig-dark">
            <InstagramGlyph size={48} tight />
          </span>
        </div>
        <h1 className="fcf-title fcf-title--badge">
          {t("fanConnect.connectTitle")}
          <VerifiedTick className="fcf-title-tick" label={t("fanConnect.verified")} />
        </h1>
        <ul className="fcf-benefits">
          {benefits.map((b) => (
            <Benefit key={b.text} text={b.text} icon={b.icon} />
          ))}
        </ul>
      </>
    ),
    footer: (
      <>
        <div className="fcf-trust">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
          </svg>
          {t("fanConnect.trust")}
        </div>
        <button type="button" className="fcf-go" onClick={onConnect || (() => void v.start())} disabled={busy ?? v.phase === "starting"}>
          {t("fanConnect.connectButton")}
          <Chevron />
        </button>
        {line && (
          <p className="fcf-note" role="status">
            {line}
          </p>
        )}
        <button type="button" className="fcf-skip" onClick={onSkip}>
          {t("fanConnect.skip")}
        </button>
      </>
    ),
  };
}

/** "Say hi on Instagram": the link to tap, and the wait. On a
    computer, the link as a QR to scan with the phone instead (as in My Profile). */
export function waitingView({ v, onCancel }: { v: InstagramDmVerify; onCancel: () => void }): IgConnectView {
  if (onComputer() && v.qrSvg) {
    return {
      back: null,
      content: (
        <>
          <div className="fcf-qr">
            <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(v.qrSvg)}`} alt="" />
            <span className="fcf-qr-logo">
              <InstagramGlyph size={34} />
            </span>
          </div>
          <h1 className="fcf-title fcf-title--wait">{t("fanConnect.scanTitle")}</h1>
          <p className="fcf-sub">{t("fanConnect.scanSub")}</p>
          <div className="fcf-wait" role="status">
            <span className="fcf-spinner" aria-hidden="true" />
            {t("fanConnect.waiting")}
          </div>
        </>
      ),
      footer: (
        <button type="button" className="fcf-quiet" onClick={onCancel}>
          {t("fanConnect.cancel")}
        </button>
      ),
    };
  }
  const native = Capacitor.isNativePlatform();
  const openInstagram = (e: React.MouseEvent) => {
    if (native && v.url) {
      e.preventDefault();
      openInstagramInApp(v.url);
    }
  };
  return {
    back: null,
    content: (
      <>
        <div className="fcf-ig-msg">
          <InstagramGlyph size={96} tight />
          {/* v3 (founder 10/30): a paper plane, "send", with a thin green fold. */}
          <span className="fcf-msg-badge" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24">
              <g transform="translate(-0.6 0.6)">
                <path fill="#fff" d="M20.9 2.6 3.4 9.7c-.8.3-.8 1.5.1 1.7l6.6 2 2 6.6c.2.9 1.4.9 1.7.1l7.1-17.5c.2-.4-.3-.9-.7-.7Z" />
                <path d="m9.6 14.2 5.6-5.6" fill="none" stroke="#16a34a" strokeWidth="1.9" strokeLinecap="round" />
              </g>
            </svg>
          </span>
        </div>
        <h1 className="fcf-title fcf-title--wait">{t("fanConnect.waitTitle")}</h1>
        <p className="fcf-sub">
          <T k="fanConnect.anyMessage" vars={{ handle: v.handle }} tags={{ 0: (c) => <b>{c}</b> }} />
        </p>
        <div className="fcf-wait" role="status">
          <span className="fcf-spinner" aria-hidden="true" />
          {t("fanConnect.waiting")}
        </div>
      </>
    ),
    footer: (
      <>
        <a className="fcf-go" href={v.url || undefined} target="_blank" rel="noopener noreferrer" onClick={openInstagram}>
          {t("fanConnect.openInstagram")}
        </a>
        <button type="button" className="fcf-quiet" onClick={onCancel}>
          {t("fanConnect.cancel")}
        </button>
      </>
    ),
  };
}

/** The handle Instagram vouched for, on one line: the name shrinks to fit
    (20px down to 12px; the card may widen to the side margins first), never
    the Instagram glyph, the rosette or the padding; "…" only past 12px. */
const HANDLE_MIN_PX = 12;
const HandleCard: React.FC<{ username: string }> = ({ username }) => {
  const ref = useRef<HTMLSpanElement>(null);
  useFitText(ref, username, HANDLE_MIN_PX);
  return (
    <div className="fcf-handle-card">
      <InstagramGlyph size={30} />
      <span ref={ref} className="fcf-handle" title={`@${username}`}>
        @{username}
      </span>
      <VerifiedTick className="fcf-handle-tick" label={t("fanConnect.verified")} />
    </div>
  );
};

/** "Instagram connected!": the handle Instagram vouched for, with the rosette.
    No line under the title (v3, founder 10/30). */
export function doneView({
  username,
  continueLabel,
  onContinue,
}: {
  username: string;
  continueLabel: string;
  onContinue: () => void;
}): IgConnectView {
  return {
    back: null,
    content: (
      <>
        <VerifiedCelebration />
        <h1 className="fcf-title">{t("fanConnect.doneTitle")}</h1>
        <HandleCard username={username} />
      </>
    ),
    footer: (
      <button type="button" className="fcf-go vc-after" onClick={onContinue}>
        {continueLabel}
      </button>
    ),
  };
}

/** "That took too long": the waiting screen's Instagram, its message badge
    turned into a quiet "try again" one; Try again in the same green as every
    other step, and "Skip for now" under it (founder, rounds 6–7). */
export function expiredView({ v, onBack, onSkip }: { v: InstagramDmVerify; onBack: () => void; onSkip: () => void }): IgConnectView {
  return {
    back: onBack,
    content: (
      <>
        <div className="fcf-ig-msg">
          <InstagramGlyph size={96} tight />
          <span className="fcf-retry-badge" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e5e7eb" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12a7 7 0 1 1-2.05-4.95" />
              <path d="M19.5 4.5v4h-4" />
            </svg>
          </span>
        </div>
        <h1 className="fcf-title fcf-title--wait">{t("fanConnect.expiredTitle")}</h1>
        <p className="fcf-sub fcf-sub--tight">{t("fanConnect.expiredSub")}</p>
        <p className="fcf-sub fcf-sub--again">{t("fanConnect.expiredAgain")}</p>
      </>
    ),
    footer: (
      <>
        <button type="button" className="fcf-go" onClick={() => void v.start()} disabled={v.phase === "starting"}>
          {t("fanConnect.tryAgain")}
        </button>
        {v.phase === "failed" && <p className="fcf-note">{t("fanConnect.failed")}</p>}
        <button type="button" className="fcf-skip" onClick={onSkip}>
          {t("fanConnect.skip")}
        </button>
      </>
    ),
  };
}
